// Shared types for the job description writer wizard.

export type Tone = 'warm' | 'professional' | 'energetic' | 'concise';

export type BenefitKey = 'medical' | 'dental' | 'vision' | '401k' | 'pto' | 'bonuses';

export type AnswerValue = string | string[] | undefined;

// Every answer is optional. The server bounds them: q1/q6 up to 1000
// characters, q2/q3 up to 12 items of 100 characters.
export interface Answers {
  q1?: string;          // #1 reason to love working here
  q2?: string[];        // day-to-day responsibilities
  q3?: string[];        // must-have skills / software
  q4?: Tone;            // tone
  q5?: BenefitKey[];    // stand-out benefits requested (optional pre-selection)
  q6?: string;          // anything unique
}

export type QuestionType = 'text' | 'textarea' | 'chips' | 'tone' | 'benefits';

export interface WizardQuestion {
  id: keyof Answers;
  type: QuestionType;
  label: string;
  helper?: string;
  placeholder?: string;
  maxLength?: number;
  maxItems?: number;
  minItems?: number;
  suggestions?: string[];
  required?: boolean;
}

// Job form fields sent with every request, since the job may not be saved
// yet. All optional; `job_id` loads a saved job and the body overrides it.
export interface JobFields {
  job_id?: number;
  job_title?: string;
  position_type?: string;
  compensation_type?: string;
  compensation?: number | string | null;
  min_compensation?: number | string | null;
  exact_pay?: boolean;
  doe?: boolean;
  start_date?: string | null;
  temp_date?: string | null;
  temp_start?: string | null;
  temp_end?: string | null;
  schedule?: string;
  years_experience?: string;
  skill_ids?: number[];
  benefits?: BenefitKey[];
  confidential?: boolean;
  description?: string;
}

export const EXTRA_INFO_MAX_LENGTH = 2000;

export interface GenerateRequest extends JobFields {
  practice_id?: number;
  job_type_id?: number;
  answers?: Answers;
  extra_info?: string;
  regenerate_hint?: string;
  draft_id?: number;
}

export interface GenerateSuccess {
  success: true;
  draft_id: number;
  description: string;
  description_html?: string;
  title: string | null;
  experience_required: string | null;
  benefits: BenefitKey[];
  tokens: number | null;
  regenerate_count: number;
}

export type GenerateErrorCode =
  | 'invalid_input'
  | 'ai_unavailable'
  | 'internal_error'
  | 'feature_disabled'
  | 'not_practice_user'
  | 'forbidden_practice'
  | 'forbidden_draft'
  | 'forbidden_job'
  | 'not_found'
  | 'rate_limited'
  | 'unauthenticated'
  | 'unknown';

export interface GenerateError {
  success: false;
  error: string;
  code: GenerateErrorCode;
  limit?: number;
  scope?: 'hourly' | 'daily' | 'global_daily';
  details?: string[];
}

export type GenerateResult = GenerateSuccess | GenerateError;
